import {
  RiAlarmWarningLine,
  RiDashboard3Line,
  RiDatabase2Line,
  RiFileTextLine,
  RiFunctions,
  RiHome2Line,
  RiInformationLine,
  RiLayoutMasonryLine,
  RiLineChartLine,
  RiMapPinLine,
  RiPlugLine,
  RiPriceTag3Line,
  RiPulseLine,
  RiRfidFill,
  RiScales3Line,
  RiSettings4Line,
  RiTeamLine,
  RiUserSettingsLine,
  type RemixiconComponentType,
} from '@remixicon/react';
import {
  hasPermission,
  type AccessibleTenant,
  type AdminModule,
  type MeResponse,
  type MonitoringAction,
  type MonitoringMenu,
  type PermissionSet,
} from '@atnt/shared';

/**
 * Navigation model of the "Site bar" tree (03-modules §1.2) and the route → workspace-tab registry (§1.5).
 * Labels are i18n keys of the `shell` namespace; Uzbek strings are verbatim from the design.
 */

/** Tenant-scoped pages reachable from the sidebar (all take the `tenantSlug` param only). */
export type TenantLeafRoute =
  | '/t/$tenantSlug/dashboard/edit'
  | '/t/$tenantSlug/devices/config'
  | '/t/$tenantSlug/devices/categories'
  | '/t/$tenantSlug/devices/locations'
  | '/t/$tenantSlug/devices/info'
  | '/t/$tenantSlug/devices/calibration'
  | '/t/$tenantSlug/devices/charts'
  | '/t/$tenantSlug/monitoring/devices'
  | '/t/$tenantSlug/monitoring/physical'
  | '/t/$tenantSlug/monitoring/virtual'
  | '/t/$tenantSlug/employees'
  | '/t/$tenantSlug/connectors'
  | '/t/$tenantSlug/documents'
  | '/t/$tenantSlug/alarms';

export interface NavLeaf {
  id: string;
  labelKey: string;
  icon: RemixiconComponentType;
  /** glyph colour (palette class) */
  iconClass: string;
  to: TenantLeafRoute;
  /** path after `/t/:slug/`, used for active matching and guards */
  segment: string;
  visible(p: PermissionSet): boolean;
}

export interface NavGroup {
  id: 'devices' | 'monitoring' | 'employees' | 'connectors' | 'documents' | 'alarms' | 'constructor';
  labelKey: string;
  icon: RemixiconComponentType;
  /** chip fill (palette class) */
  chipClass: string;
  leaves: NavLeaf[];
  /** the constructor group also lists the tenant's sections (loaded from the API) */
  withSections?: boolean;
}

const admin = (module: AdminModule) => (p: PermissionSet) => hasPermission(p, { panel: 'admin', module, action: 'read' });
const monitoring =
  (menu: MonitoringMenu, action: MonitoringAction = 'view') =>
  (p: PermissionSet) =>
    hasPermission(p, { panel: 'monitoring', menu, action });

/** Section editors: same rule as the API (`sections.edit` or `dashboard.edit`). */
export const canEditSections = (p: PermissionSet) => monitoring('sections', 'edit')(p) || monitoring('dashboard', 'edit')(p);

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'devices',
    labelKey: 'nav.groups.devices',
    icon: RiRfidFill,
    chipClass: 'bg-dark-blue-1000',
    leaves: [
      { id: 'device-config', labelKey: 'nav.items.deviceConfig', icon: RiSettings4Line, iconClass: 'text-dark-blue-1000', to: '/t/$tenantSlug/devices/config', segment: 'devices/config', visible: admin('device_config') },
      { id: 'device-categories', labelKey: 'nav.items.deviceCategories', icon: RiPriceTag3Line, iconClass: 'text-blue-1000', to: '/t/$tenantSlug/devices/categories', segment: 'devices/categories', visible: admin('device_categories') },
      { id: 'device-locations', labelKey: 'nav.items.deviceLocations', icon: RiMapPinLine, iconClass: 'text-green-1000', to: '/t/$tenantSlug/devices/locations', segment: 'devices/locations', visible: admin('device_locations') },
      { id: 'device-info', labelKey: 'nav.items.deviceInfo', icon: RiInformationLine, iconClass: 'text-orange-1000', to: '/t/$tenantSlug/devices/info', segment: 'devices/info', visible: admin('device_info') },
      { id: 'device-calibration', labelKey: 'nav.items.deviceCalibration', icon: RiScales3Line, iconClass: 'text-purple-1000', to: '/t/$tenantSlug/devices/calibration', segment: 'devices/calibration', visible: admin('device_calibration') },
      { id: 'charts', labelKey: 'nav.items.charts', icon: RiLineChartLine, iconClass: 'text-white-900', to: '/t/$tenantSlug/devices/charts', segment: 'devices/charts', visible: monitoring('charts') },
    ],
  },
  {
    // [P] 03-modules §1.2: yellow + pulse-line instead of the design's placeholder copy of "Xodimlar"
    id: 'monitoring',
    labelKey: 'nav.groups.monitoring',
    icon: RiPulseLine,
    chipClass: 'bg-yellow-1000',
    leaves: [
      { id: 'monitoring-devices', labelKey: 'nav.items.monitoringDevices', icon: RiDatabase2Line, iconClass: 'text-yellow-1000', to: '/t/$tenantSlug/monitoring/devices', segment: 'monitoring/devices', visible: admin('devices') },
      { id: 'monitoring-physical', labelKey: 'nav.items.monitoringPhysical', icon: RiDashboard3Line, iconClass: 'text-yellow-1000', to: '/t/$tenantSlug/monitoring/physical', segment: 'monitoring/physical', visible: admin('devices') },
      { id: 'monitoring-virtual', labelKey: 'nav.items.monitoringVirtual', icon: RiFunctions, iconClass: 'text-yellow-1000', to: '/t/$tenantSlug/monitoring/virtual', segment: 'monitoring/virtual', visible: admin('devices') },
    ],
  },
  {
    id: 'employees',
    labelKey: 'nav.groups.employees',
    icon: RiTeamLine,
    chipClass: 'bg-dark-green-1000',
    leaves: [
      { id: 'employees', labelKey: 'nav.items.employees', icon: RiUserSettingsLine, iconClass: 'text-dark-green-1000', to: '/t/$tenantSlug/employees', segment: 'employees', visible: admin('employees') },
    ],
  },
  {
    id: 'connectors',
    labelKey: 'nav.groups.connectors',
    icon: RiPlugLine,
    chipClass: 'bg-pink-1000',
    leaves: [
      { id: 'connectors', labelKey: 'nav.items.connectors', icon: RiPlugLine, iconClass: 'text-pink-1000', to: '/t/$tenantSlug/connectors', segment: 'connectors', visible: admin('connectors') },
    ],
  },
  {
    // [P] 03-modules §1.2 "Hujjatlar" group
    id: 'documents',
    labelKey: 'nav.groups.documents',
    icon: RiFileTextLine,
    chipClass: 'bg-orange-1000',
    leaves: [
      // "Hisobotlar" is a sub-tab of the same page, so either library makes the leaf visible
      { id: 'documents', labelKey: 'nav.items.documents', icon: RiFileTextLine, iconClass: 'text-orange-1000', to: '/t/$tenantSlug/documents', segment: 'documents', visible: (p) => monitoring('documents')(p) || monitoring('reports')(p) },
    ],
  },
  {
    // [P] 03-modules §8.2 "Mezonlar": alarm events for monitoring viewers (+ sound settings for reservoir admins)
    id: 'alarms',
    labelKey: 'nav.groups.alarms',
    icon: RiAlarmWarningLine,
    chipClass: 'bg-tone-red',
    leaves: [
      { id: 'alarms', labelKey: 'nav.items.alarms', icon: RiAlarmWarningLine, iconClass: 'text-tone-red', to: '/t/$tenantSlug/alarms', segment: 'alarms', visible: (p) => monitoring('dashboard')(p) || monitoring('sections')(p) || monitoring('charts')(p) },
    ],
  },
  {
    // [P] entry to the constructor: dashboard editor + one leaf per section (section editors)
    id: 'constructor',
    labelKey: 'nav.groups.constructor',
    icon: RiLayoutMasonryLine,
    chipClass: 'bg-blue-1000',
    withSections: true,
    leaves: [
      { id: 'dashboard-edit', labelKey: 'nav.items.dashboard', icon: RiHome2Line, iconClass: 'text-blue-1000', to: '/t/$tenantSlug/dashboard/edit', segment: 'dashboard/edit', visible: monitoring('dashboard', 'edit') },
    ],
  },
];

/** Groups of a tenant with their visible leaves; groups without any visible entry are dropped. */
export function visibleGroups(permissions: PermissionSet) {
  return NAV_GROUPS.flatMap((g) => {
    const leaves = g.leaves.filter((l) => l.visible(permissions));
    const sections = Boolean(g.withSections) && canEditSections(permissions);
    return leaves.length || sections ? [{ ...g, leaves, sections }] : [];
  });
}
export type VisibleGroup = ReturnType<typeof visibleGroups>[number];

/** `/t/:slug/<rest>` → { slug, rest } */
export function splitTenantPath(pathname: string): { slug: string; rest: string } | null {
  const m = /^\/t\/([^/]+)\/?(.*)$/.exec(pathname);
  return m ? { slug: decodeURIComponent(m[1]!), rest: m[2]!.replace(/\/+$/, '') } : null;
}

const startsWithSegment = (rest: string, segment: string) => rest === segment || rest.startsWith(segment + '/');

export const isPlatformOrOrgAdmin = (me: MeResponse) => me.isPlatformAdmin || me.organizations.some((o) => o.isAdmin);

/**
 * Route guard shared by the shell layouts (03-modules §1.6): `false` when the current page belongs to a sidebar
 * entry the principal cannot see. Unknown pages are allowed (features guard their own details).
 */
export function canAccessPath(pathname: string, me: MeResponse, tenant?: AccessibleTenant): boolean {
  const tp = splitTenantPath(pathname);
  if (tp && tenant) {
    if (/^sections\/[^/]+\/edit/.test(tp.rest)) return canEditSections(tenant.permissions);
    for (const g of NAV_GROUPS) {
      for (const l of g.leaves) if (startsWithSegment(tp.rest, l.segment)) return l.visible(tenant.permissions);
    }
    return true;
  }
  if (/^\/admin(\/|$)/.test(pathname)) return isPlatformOrOrgAdmin(me);
  return true;
}

/** Workspace-tab glyph: a plain 18 px icon, or a 20 px coloured chip when `color` is set (01-design-system §6.3). */
export interface TabIcon {
  name: string;
  color?: string;
}

export interface TabSpec {
  key: string;
  titleKey: string;
  icon: TabIcon;
  tenantSlug?: string;
}

const MODULE_CHIP: TabIcon = { name: 'file-line', color: '#f48200' }; // admin modules (497:21087, 556:16484)
const CONSTRUCTOR_CHIP: TabIcon = { name: 'water-flash-line', color: '#1ecd15' }; // constructor / charts (317:7103, 565:*)
const ALARMS_CHIP: TabIcon = { name: 'alarm-warning-line', color: '#ca3b3d' }; // [P] "Mezonlar" group chip (03-modules §8.2)

/**
 * Route → workspace tab (03-modules §1.5): one tab per module, so every page of a module shares the tab
 * (e.g. all device-registry pages live in "Qurilma konfiguratsiya").
 */
export function resolveTab(pathname: string): TabSpec | null {
  const tp = splitTenantPath(pathname);
  if (tp) {
    const { slug, rest } = tp;
    const tab = (id: string, titleKey: string, icon: TabIcon): TabSpec => ({ key: `${slug}:${id}`, titleKey, icon, tenantSlug: slug });
    if (rest === '') return tab('home', 'tabs.home', { name: 'home-2-line' });
    if (startsWithSegment(rest, 'dashboard/edit')) return tab('dashboard', 'tabs.dashboardEdit', CONSTRUCTOR_CHIP);
    const section = /^sections\/([^/]+)/.exec(rest);
    if (section) return tab(`section:${section[1]}`, 'tabs.sectionEdit', CONSTRUCTOR_CHIP);
    const viewer = /^s\/([^/]+)/.exec(rest);
    if (viewer) return tab(`view:${viewer[1]}`, 'tabs.section', CONSTRUCTOR_CHIP);
    if (startsWithSegment(rest, 'devices/charts')) return tab('charts', 'tabs.charts', CONSTRUCTOR_CHIP);
    if (startsWithSegment(rest, 'devices')) return tab('devices', 'tabs.devices', MODULE_CHIP);
    if (startsWithSegment(rest, 'monitoring')) return tab('monitoring', 'tabs.monitoring', MODULE_CHIP);
    if (startsWithSegment(rest, 'employees')) return tab('employees', 'tabs.employees', MODULE_CHIP);
    if (startsWithSegment(rest, 'connectors')) return tab('connectors', 'tabs.connectors', MODULE_CHIP);
    if (startsWithSegment(rest, 'documents')) return tab('documents', 'tabs.documents', MODULE_CHIP);
    if (startsWithSegment(rest, 'alarms')) return tab('alarms', 'tabs.alarms', ALARMS_CHIP);
    return null;
  }
  if (/^\/settings(\/|$)/.test(pathname)) return { key: 'settings', titleKey: 'tabs.settings', icon: MODULE_CHIP };
  if (/^\/guide(\/|$)/.test(pathname)) return { key: 'guide', titleKey: 'tabs.guide', icon: MODULE_CHIP };
  if (/^\/admin(\/|$)/.test(pathname)) return { key: 'admin', titleKey: 'tabs.admin', icon: MODULE_CHIP };
  return null;
}
